import { useEnterAnimation } from "@/pages/services/hooks/useEnterAnimation";

export interface ServicePanelData {
  index: string; // "01" / "02" / "03"
  title: string;
  kicker: string; // italic tagline under the title
  guide: string; // "For you, if…" line
  paragraph: string; // 2–3 sentence description
  image: string;
  bookHref: string;
}

/**
 * ServicePanel — one full-screen service block.
 *
 * Layout mirrors the home Services split: LEFT is a full-bleed image with a
 * bottom-up dark gradient overlay and white text baked into the image side
 * (satisfies the "no white-card frames" rule). RIGHT column holds the
 * numbered title, kicker line, guide sentence and paragraph.
 *
 * A single deep-charcoal capsule CTA (matches the home "Book a Session"
 * treatment: dark pill + primary-500 dot + white arrow) sits at the bottom
 * of the right column.
 *
 * Enter animation: translateY 30 → 0 + opacity 0 → 1, staggered element by
 * element, via useRef + rAF (no useState).
 */
export default function ServicePanel({
  data,
  reverse = false,
}: {
  data: ServicePanelData;
  reverse?: boolean;
}) {
  const eyebrowRef = useEnterAnimation<HTMLDivElement>({ delay: 0 });
  const titleRef = useEnterAnimation<HTMLHeadingElement>({ delay: 120 });
  const kickerRef = useEnterAnimation<HTMLDivElement>({ delay: 260 });
  const guideRef = useEnterAnimation<HTMLDivElement>({ delay: 380 });
  const paragraphRef = useEnterAnimation<HTMLParagraphElement>({ delay: 500 });
  const ctaRef = useEnterAnimation<HTMLDivElement>({ delay: 640 });
  const imageRef = useEnterAnimation<HTMLDivElement>({ delay: 60 });

  return (
    <section
      className="relative w-full bg-background-50"
      style={{ minHeight: "100vh" }}
    >
      {/* Section meta strip */}
      <div className="absolute top-0 left-0 right-0 z-30 px-6 md:px-10 pt-14 md:pt-16 pointer-events-none">
        <div className="max-w-[1600px] mx-auto flex items-baseline justify-between">
          <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium">
            Our Services / {data.index}
          </span>
          <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium hidden md:block">
            Path {data.index}
          </span>
        </div>
      </div>

      <div className="w-full min-h-screen flex items-center px-6 md:px-10 py-28 md:py-36">
        <div
          className={`max-w-[1400px] w-full mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center ${
            reverse ? "lg:[direction:rtl]" : ""
          }`}
        >
          {/* IMAGE side — full-bleed card + dark gradient + white overlay text */}
          <div className={`lg:col-span-7 ${reverse ? "lg:[direction:ltr]" : ""}`}>
            <div
              ref={imageRef}
              className="relative w-full overflow-hidden rounded-2xl"
              style={{ aspectRatio: "5 / 6" }}
            >
              <img
                src={data.image}
                alt={`${data.title} — Serene`}
                className="absolute inset-0 w-full h-full object-cover object-center select-none"
                loading="lazy"
                decoding="async"
                draggable={false}
              />

              {/* Bottom-up dark gradient overlay */}
              <div
                aria-hidden="true"
                className="absolute inset-x-0 bottom-0"
                style={{
                  height: "70%",
                  background:
                    "linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.5) 35%, rgba(0,0,0,0) 100%)",
                }}
              />

              {/* Top-left index label (white on image) */}
              <div className="absolute top-6 left-6 right-6 flex items-center justify-between">
                <span
                  className="uppercase font-medium"
                  style={{
                    fontSize: "10px",
                    letterSpacing: "0.3em",
                    color: "rgba(255,255,255,0.85)",
                    textShadow: "0 1px 2px rgba(0,0,0,0.25)",
                  }}
                >
                  Path · {data.index}
                </span>
                <span
                  className="inline-block h-[1px] w-10"
                  style={{ background: "rgba(255,255,255,0.65)" }}
                ></span>
              </div>

              {/* Bottom copy inside image — white text + primary dot */}
              <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                <div
                  className="italic mb-4"
                  style={{
                    fontFamily: "var(--font-heading)",
                    fontSize: "clamp(1rem, 1.25vw, 1.35rem)",
                    fontWeight: 300,
                    lineHeight: 1.4,
                    color: "rgba(255,255,255,0.88)",
                    letterSpacing: "0.005em",
                  }}
                >
                  {data.kicker}
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-500"></span>
                  <span
                    className="uppercase font-medium"
                    style={{
                      fontSize: "10px",
                      letterSpacing: "0.3em",
                      color: "rgba(255,255,255,0.75)",
                    }}
                  >
                    {data.title}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* TEXT side */}
          <div
            className={`lg:col-span-5 ${reverse ? "lg:[direction:ltr]" : ""} flex flex-col`}
          >
            <div ref={eyebrowRef} className="mb-8 flex items-center gap-3">
              <span
                className="font-heading"
                style={{
                  fontSize: "clamp(2.75rem, 4vw, 4rem)",
                  fontWeight: 300,
                  letterSpacing: "-0.03em",
                  color: "oklch(var(--foreground-950) / 0.35)",
                  lineHeight: 1,
                  fontVariationSettings: "'opsz' 144",
                }}
              >
                {data.index}
              </span>
              <span className="w-12 h-[1px] bg-foreground-950/25"></span>
              <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium">
                Path {data.index}
              </span>
            </div>

            <h2
              ref={titleRef}
              className="mb-5"
              style={{
                fontFamily: "var(--font-heading)",
                fontSize: "clamp(2.5rem, 4.4vw, 4.5rem)",
                fontWeight: 300,
                letterSpacing: "-0.025em",
                lineHeight: 1.05,
                color: "oklch(var(--foreground-950))",
                fontVariationSettings: "'opsz' 144",
              }}
            >
              {data.title}
            </h2>

            <div
              ref={kickerRef}
              className="italic mb-10 max-w-[440px]"
              style={{
                fontFamily: "var(--font-heading)",
                fontSize: "clamp(1.15rem, 1.5vw, 1.5rem)",
                fontWeight: 300,
                lineHeight: 1.5,
                letterSpacing: "0.005em",
                color: "oklch(var(--foreground-950) / 0.7)",
                fontVariationSettings: "'opsz' 144",
              }}
            >
              {data.kicker}
            </div>

            <div ref={guideRef} className="mb-8 max-w-[440px]">
              <div className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/55 font-medium mb-3">
                For you, if&hellip;
              </div>
              <div
                style={{
                  fontFamily: "var(--font-heading)",
                  fontSize: "clamp(1rem, 1.15vw, 1.2rem)",
                  fontWeight: 300,
                  lineHeight: 1.55,
                  color: "oklch(var(--foreground-950) / 0.9)",
                  fontStyle: "italic",
                  letterSpacing: "0.005em",
                }}
              >
                {data.guide}
              </div>
            </div>

            <p
              ref={paragraphRef}
              className="mb-12 max-w-[460px]"
              style={{
                fontFamily: "var(--font-body)",
                fontSize: "clamp(0.95rem, 1.05vw, 1.1rem)",
                lineHeight: 1.7,
                color: "oklch(var(--foreground-950) / 0.82)",
              }}
            >
              {data.paragraph}
            </p>

            <div ref={ctaRef}>
              <a
                href={data.bookHref}
                className="group inline-flex items-center gap-3 bg-foreground-950 text-background-50 rounded-full pl-8 pr-2 py-2 whitespace-nowrap cursor-pointer transition-transform duration-300 hover:-translate-y-0.5"
              >
                <span className="text-[11px] tracking-[0.3em] uppercase font-medium">
                  Book This Path
                </span>
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 transition-transform duration-300 group-hover:translate-x-0.5">
                  <i className="ri-arrow-right-up-line text-lg"></i>
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}